<template>
  <div>
    <br />
    <h1 class="text-xs-center">Advanced: Reports</h1>

    <v-layout row wrap>
      <v-flex xs12 pa-2 class="text-xs-right">
        <v-btn color="primary" :disabled="apiInProgress" @click="requestDialog = true">
          Request Reports
        </v-btn>
      </v-flex>

      <v-flex xs12 pa-2>
        <v-card>
          <v-card-title>
            <span class="headline">Status Summary</span>
            <v-spacer />
            <v-btn icon :disabled="loadingSummary || apiInProgress" @click="loadSummary()">
              <v-icon>fal fa-sync</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-layout row wrap>
              <v-flex v-for="item in summaryCards" :key="item.key" xs12 sm6 md4 lg2 pa-1>
                <v-card>
                  <v-card-title class="subheading">{{ item.label }}</v-card-title>
                  <v-card-text>
                    <div><strong>Source:</strong> {{ item.source }}</div>
                    <div><strong>Aggregate:</strong> {{ item.aggregate }}</div>
                    <div><strong>Custom:</strong> {{ item.custom }}</div>
                  </v-card-text>
                </v-card>
              </v-flex>
            </v-layout>
          </v-card-text>
        </v-card>
      </v-flex>

      <v-flex xs12 pa-2>
        <v-card>
          <v-card-title>
            <span class="headline">Report Records</span>
            <v-spacer />
            <v-btn-toggle v-model="activeRecordType" mandatory @change="onRecordTypeChange">
              <v-btn flat value="source">Source</v-btn>
              <v-btn flat value="aggregate">Aggregate</v-btn>
              <v-btn flat value="custom">Custom</v-btn>
            </v-btn-toggle>
          </v-card-title>
          <v-card-text>
            <v-layout row wrap>
              <v-flex xs12 md4 pa-1>
                <v-select
                  v-model="filters.reportType"
                  :items="reportTypeOptions"
                  item-text="label"
                  item-value="value"
                  label="Filter Report Type"
                  clearable
                  @change="onFilterChange"
                />
              </v-flex>
              <v-flex xs12 md4 pa-1>
                <v-select
                  v-model="filters.reportStatus"
                  :items="statusOptions"
                  label="Filter Status"
                  clearable
                  @change="onFilterChange"
                />
              </v-flex>
              <v-flex xs12 md4 pa-1>
                <v-text-field
                  v-model="filters.categoryId"
                  label="Filter Category ID"
                  clearable
                  @change="onFilterChange"
                />
              </v-flex>
            </v-layout>
            <v-data-table
              :headers="tableHeaders"
              :items="records"
              :loading="loadingRecords"
              :pagination.sync="pagination"
              :total-items="totalRecords"
              :rows-per-page-items="[10, 25, 50, 100]"
              @update:pagination="onPagination"
              class="elevation-1"
              must-sort
            >
              <template v-slot:items="props">
                <tr>
                  <td>{{ props.item.reportType }}</td>
                  <td>{{ props.item.reportFidelity || 'day' }}</td>
                  <td>{{ props.item.reportStatus }}</td>
                  <td>{{ formatTimestamp(props.item.periodStart) }}</td>
                  <td>{{ formatTimestamp(props.item.periodEnd) }}</td>
                  <td>{{ props.item.category || '-' }}</td>
                  <td>{{ props.item.categoryId || '-' }}</td>
                  <td>{{ formatTimestamp(props.item.dateUpdated) }}</td>
                  <td class="text-xs-right">
                    <v-btn
                      icon
                      :disabled="loadingRecords || deletingRecordId !== null || apiInProgress"
                      :loading="isDeletingRecord(props.item)"
                      @click="deleteReportRecord(props.item)"
                    >
                      <v-icon small>fa fa-trash-alt</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>

    <v-dialog v-model="requestDialog" max-width="1000">
      <v-card>
        <v-card-title>
          <span class="headline">Request Reports</span>
          <v-spacer />
          <v-btn icon :disabled="apiInProgress" @click="requestDialog = false">
            <v-icon>fal fa-times</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-layout row wrap>
            <v-flex xs12 md4 pa-1>
              <v-select
                v-model="requestForm.reportType"
                :items="reportTypeOptions"
                item-text="label"
                item-value="value"
                label="Report Type"
                :disabled="apiInProgress"
                @change="onReportTypeChange"
              />
            </v-flex>
            <v-flex xs12 md4 pa-1>
              <v-select
                v-model="requestForm.target"
                :items="targetOptions"
                label="Target Table"
                :disabled="apiInProgress"
              />
            </v-flex>
            <v-flex xs12 md4 pa-1>
              <v-select
                v-model="requestForm.reportFidelity"
                :items="fidelityOptions"
                label="Report Fidelity"
                :disabled="apiInProgress || requestForm.target !== 'custom'"
              />
            </v-flex>
            <v-flex xs12 md4 pa-1>
              <v-text-field
                v-model="requestForm.category"
                label="Category"
                :disabled="apiInProgress"
              />
            </v-flex>
            <v-flex xs12 md4 pa-1>
              <v-text-field
                v-model="requestForm.categoryId"
                label="Category ID"
                :disabled="apiInProgress"
              />
            </v-flex>
            <v-flex xs12 md4 pa-1>
              <v-switch
                v-model="requestForm.recalculate"
                label="Recalculate Existing"
                :disabled="apiInProgress"
              />
            </v-flex>
            <v-flex xs12 md6 pa-1>
              <v-text-field
                label="Date Start (UTC)"
                type="date"
                v-model="requestForm.dateStart"
                :append-icon="requestForm.dateStart ? 'fal fa-times' : ''"
                :disabled="apiInProgress"
                hide-details
                @click:append="requestForm.dateStart = null"
              />
            </v-flex>
            <v-flex xs12 md6 pa-1>
              <v-text-field
                label="Date End (UTC)"
                type="date"
                v-model="requestForm.dateEnd"
                :append-icon="requestForm.dateEnd ? 'fal fa-times' : ''"
                :disabled="apiInProgress"
                hide-details
                @click:append="requestForm.dateEnd = null"
              />
            </v-flex>
          </v-layout>
          <div class="mt-2">
            <v-btn color="primary" :disabled="!canSubmitReportRequest" @click="submitReportRequest">Request Reports</v-btn>
            <v-btn flat :disabled="apiInProgress" @click="requestDialog = false">Cancel</v-btn>
          </div>
          <div v-if="requestResultMessage" class="mt-2">{{ requestResultMessage }}</div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import src from './src'
export default src
</script>
